import type { BriefingFacts, CapacityOperands } from "@hakili/contracts";
import { formatDateTime, t } from "@hakili/i18n";
import { ActionBar, BANK_FS_META, Button, Callout, Card, StatCard } from "@hakili/ui";
import { AlertTriangle, ArrowRight, Info } from "lucide-react";
import { type ReactElement, useState } from "react";

import { LabelledSentence } from "../labelled-sentence.js";
import { asSentence } from "../sentence.js";
import { COUNT_WORDS, type AnalysisPointView, type AnalysisView } from "./analyse.js";
import { fill } from "./dossiers.js";
import { RefuseModal } from "./revue.js";
import type { RunStanding } from "./underwriter-bay.js";

/**
 * `A-1b-1` (D3 row A-1b, D0 A1 « Nouveau », D2-copy « Nouveau », the architect's ruling on M0): **the landing is a briefing**, from the
 * bank's own door and nothing else. The reception line (its typed part), the verdict line (the letter, the average, the number of points
 * the run proposed), three tiles each with its precision, « Ce qui demande votre attention » (the Capacité line with the run's ONE term, and
 * each blocking finding in the server's sentence), the avis, and « Prendre en revue ». Hakili is shown only doing what D0 §1.1 lets the
 * design show: reading, computing a grade with its detail, the avis, a run that cannot finish saying so.
 *
 * **What has no door is not drawn (D-1019-2).** `A-1b-1b` binds the facts that now have one: `briefing` (BRIEFING-FIELDS, #642) for the counts, the
 * fourth tile, the apport's precision and the pièces read, and `capacityVerdict` / `capacityWords` (CAPACITY-VERDICT, #656) for the Capacité line, which prints
 * only where the server says « exceeds » and carries the server's sentence, never a word the screen chose. « dossier complet » and the second term
 * (« 60 mois (durée maximale) … Hakili recommande 60 mois ») have no field and are not drawn. « Refuser » is not drawn here yet: `POST …/refuse`
 * answers at `nouveau` and `revue` since D-1026-AA-4 (`bank-refusal.controller.ts`, `REFUSABLE_STAGES`); the button is its own round. Nothing is computed in the browser.
 */
export interface BriefingFile {
  /** The instant the file was received (the submission's own, else the stage's). */
  readonly receivedAt: string;
  readonly depositPct: number | null;
}

const tenths = (value: number): string => {
  const [whole = "0", fraction = "0"] = String(value).split(".");
  return `${whole},${fraction}`;
};
/** D2 `ui.js:27` `INC`: the capacity's cause in D2's label, keyed by the server's typed cause (`underwriting-grade.ts` `CapacityCause`). */
export const CAUSE_LABELS = new Map<string, string>([
  ["grille", t("bank.briefing.causeGrille")],
  ["quotiteCessible", t("bank.briefing.causeQuotite")],
  ["revenuNet", t("bank.briefing.causeSalaire")],
  ["engagements", t("bank.briefing.causeEngagements")],
  ["tauxMandat", t("bank.briefing.causeMandat")],
  ["inconnue", t("bank.briefing.causeInconnueCapacite")],
]);

/**
 * D2 `ui.js:27` (`INC`) and `analyst.js:62`: the sentence D2 draws for a capacity the run could not compute, by cause. Mapped client-side from the
 * code; the server's own words are NOT altered or compared (no prefix stripping), they simply are not printed beside D2's sentence. A cause with
 * no entry here (`tauxMandat`, `inconnue`: ruled) keeps the server's words.
 */
const CAUSE_SENTENCES = new Map<string, { readonly why: string; readonly policy: boolean }>([
  ["grille", { why: t("bank.briefing.causeGrilleExplique"), policy: true }],
  ["quotiteCessible", { why: t("bank.briefing.causeQuotiteExplique"), policy: false }],
  ["revenuNet", { why: t("bank.briefing.causeSalaireExplique"), policy: false }],
  ["engagements", { why: t("bank.briefing.causeEngagementsExplique"), policy: false }],
]);

/** D2 \`.tile .v{white-space:nowrap}\` (\`components.css:102\`): a tile's value is one line, « non calculée » included. */
const unbreakable = (value: string): ReactElement => <span className="whitespace-nowrap">{value}</span>;

const pointOf = (analysis: AnalysisView, code: string): AnalysisPointView | undefined =>
  analysis.points.find((one) => one.code === code);

const operandsOf = (analysis: AnalysisView): CapacityOperands | null => {
  const evidence = pointOf(analysis, "quotiteCessible")?.evidence;
  if (evidence?.kind !== "figures") return null;
  const operands = evidence.operands;
  return operands?.notComputed === null && operands.capacityUsedPct !== null ? operands : null;
};

const termWords = (operands: CapacityOperands): string | null =>
  operands.termMonths === null || operands.termBasis === null
    ? null
    : fill("bank.briefing.dureeMois", {
        n: String(operands.termMonths),
        base: t(
          operands.termBasis === "asked" ? "bank.briefing.dureeDemandee" : "bank.briefing.dureeMaximale",
        ),
      });

/** The door's own words label the compared figures (`bank-view-words.ts`); the catalogue holds them. */
const comparedOf = (analysis: AnalysisView, code: string, label: string): string | null => {
  const evidence = pointOf(analysis, code)?.evidence;
  if (evidence?.kind !== "figures") return null;
  const value = evidence.compared[label];
  return value === undefined || value === null ? null : String(value);
};

/** The words of a cause code the server sends (`non_lu`, `inconnue`): literal keys, so the catalogue's guard sees them. PROPOSITION: the designer's answer binds the words. */
const CAUSE_WORDS = new Map<string, string>([
  ["non_lu", t("bank.briefing.causeNonLu")],
  ["inconnue", t("bank.briefing.causeInconnue")],
]);

/** The four sub-reasons of an apport the reader flagged (workflow v3 §3.6). PROPOSITION, like the cause words. */
const SUB_REASON_WORDS = new Map<string, string>([
  ["apport_non_visible", t("bank.briefing.apportNonVisible")],
  ["origine_non_coherente", t("bank.briefing.origineNonCoherente")],
  ["apport_emprunte", t("bank.briefing.apportEmprunte")],
  ["origine_a_cribler", t("bank.briefing.origineACribler")],
]);

const causeWords = (cause: string | null): string | null =>
  cause === null ? null : (CAUSE_WORDS.get(cause) ?? null);

/** « {n} pièces lues » from the server's count; a count the run does not hold reads « nombre de pièces lues non conservé » (the designer's words), never a 0. */
const documentsReadWords = (facts: BriefingFacts): string => {
  const { count } = facts.documentsRead;
  return count === null
    ? t("bank.briefing.piecesLuesInconnu")
    : count === 1
      ? t("bank.briefing.pieceLueUne")
      : fill("bank.briefing.piecesLues", { n: String(count) });
};

/**
 * The Apport tile's precision, one line each in the tile's own hint box: the evidenced amount, then the reason the reader flagged on its OWN line;
 * « non visible sur le compte » and an amount would contradict, so that reason prints alone. With neither, the cause when the run read none.
 */
function ApportHint({ facts }: { readonly facts: BriefingFacts }): ReactElement | null {
  const { evidencedAmount, subReason, cause } = facts.apport;
  const reason = subReason === null ? null : (SUB_REASON_WORDS.get(subReason) ?? null);
  const amountLine =
    evidencedAmount === null || subReason === "apport_non_visible"
      ? null
      : t("bank.briefing.apportVisibleCompte");
  const why = amountLine === null && reason === null ? causeWords(cause) : null;
  if (amountLine === null && reason === null && why === null) return null;
  return (
    <>
      {amountLine === null ? null : (
        <span className="block" data-testid="bank-briefing-tuile-apport-montant">
          {amountLine}
        </span>
      )}
      {reason === null ? null : (
        <span className="block" data-testid="bank-briefing-tuile-apport-raison">
          {reason}
        </span>
      )}
      {why === null ? null : <span className="block">{why}</span>}
    </>
  );
}

function IncidentsTile({ facts }: { readonly facts: BriefingFacts }): ReactElement {
  const { count, statementsRead, cause } = facts.incidents;
  const hint =
    count === null
      ? causeWords(cause)
      : statementsRead === null
        ? null
        : statementsRead === 1
          ? t("bank.briefing.incidentsReleveUn")
          : fill("bank.briefing.incidentsReleves", { n: String(statementsRead) });
  return (
    <div className="flex min-w-0" data-testid="bank-briefing-tuile-incidents">
      <StatCard
        className="min-w-0 flex-1"
        label={t("bank.briefing.tuileIncidents")}
        value={unbreakable(count === null ? t("common.valeur.tiret") : String(count))}
        {...(hint === null ? {} : { hint })}
      />
    </div>
  );
}

function CapacityTile({ analysis }: { readonly analysis: AnalysisView }): ReactElement | null {
  const incomplete = analysis.grade?.incomplete ?? null;
  if (incomplete !== null) {
    return (
      <div className="flex min-w-0" data-testid="bank-briefing-tuile-quotite">
        <StatCard
          className="min-w-0 flex-1"
          label={t("bank.briefing.tuileQuotite")}
          value={unbreakable(t("bank.briefing.tuileNonCalculee"))}
          tone="hot"
          {...(CAUSE_LABELS.get(incomplete.cause) === undefined
            ? {}
            : { hint: <span className={BANK_FS_META}>{CAUSE_LABELS.get(incomplete.cause)}</span> })}
        />
      </div>
    );
  }
  const operands = operandsOf(analysis);
  if (operands?.capacityUsedPct === null || operands === null) return null;
  const term = termWords(operands);
  const partial = analysis.gridMissingGrades.length > 0 && operands.rateSource?.kind === "highestRow";
  const rate =
    partial && operands.rateSource?.kind === "highestRow" && operands.ratePct !== null
      ? fill("bank.briefing.tauxLigneHaute", {
          note: operands.rateSource.grade,
          taux: operands.ratePct.replace(".", ","),
        })
      : null;
  const hint = [term === null ? null : fill("bank.briefing.precisionDuree", { duree: term }), rate]
    .filter((one): one is string => one !== null)
    .join(", ");
  return (
    <div className="flex min-w-0" data-testid="bank-briefing-tuile-quotite">
      <StatCard
        className="min-w-0 flex-1"
        label={t("bank.briefing.tuileQuotite")}
        value={unbreakable(fill("bank.briefing.pct", { n: tenths(operands.capacityUsedPct) }))}
        {...(hint === "" ? {} : { hint })}
        {...(operands.capacityUsedPct > 100 ? { tone: "hot" as const } : {})}
      />
    </div>
  );
}

function Attention({ analysis }: { readonly analysis: AnalysisView }): ReactElement {
  const incomplete = analysis.grade?.incomplete ?? null;
  const operands = operandsOf(analysis);
  const blocking = (analysis.registre ?? []).filter((one) => one.severity === "bloquant");
  const exceeds = operands?.capacityVerdict === "exceeds";
  const facts = analysis.briefing;
  const points = facts?.pointsToCheck.read ?? 0;
  const letter = analysis.grade?.grade ?? null;
  const missing = analysis.gridMissingGrades;
  const highest = operands?.rateSource?.kind === "highestRow" ? operands.rateSource : null;
  // D2 `stageNouveau`: « Hakili propose N mois (…) » or « Pas d'offre : … », from the run's own offer, in the server's figures and words.
  const offer = analysis.offer;
  const proposed =
    offer !== null
      ? offer.termBasisWords === null || offer.termBasisWords === ""
        ? fill("bank.briefing.proposeDureeSeule", { n: String(offer.durationMonths) })
        : fill("bank.briefing.proposeDuree", { n: String(offer.durationMonths), base: offer.termBasisWords })
      : analysis.offerReason === null
        ? null
        : fill("bank.briefing.pasDOffre", { raison: asSentence(analysis.offerReason).replace(/\.$/u, "") });
  const capacity =
    incomplete !== null ? (
      <Callout
        tone="warn"
        icon={<AlertTriangle className="h-5 w-5" aria-hidden="true" />}
        testId="bank-briefing-attention-capacite"
      >
        {CAUSE_SENTENCES.has(incomplete.cause) ? (
          <>
            <span className="font-semibold">
              {fill("bank.briefing.capaciteNonCalculeeCause", {
                cause: CAUSE_LABELS.get(incomplete.cause) ?? "",
              })}
            </span>{" "}
            {CAUSE_SENTENCES.get(incomplete.cause)?.why} {t("bank.briefing.pasDeNotePasDOffre")}
            {CAUSE_SENTENCES.get(incomplete.cause)?.policy === true
              ? ` ${t("bank.briefing.chefSaisitLaGrille")}`
              : ""}
          </>
        ) : (
          <LabelledSentence
            label={
              CAUSE_LABELS.get(incomplete.cause) === undefined
                ? t("bank.hakili.capaciteNonCalculee")
                : fill("bank.briefing.capaciteNonCalculeeCause", {
                    cause: CAUSE_LABELS.get(incomplete.cause) ?? "",
                  })
            }
            words={incomplete.words}
          />
        )}
      </Callout>
    ) : operands?.capacityUsedPct !== null && operands !== null && exceeds ? (
      <Callout
        tone="warn"
        icon={<AlertTriangle className="h-5 w-5" aria-hidden="true" />}
        testId="bank-briefing-attention-capacite"
      >
        <span className="font-semibold">{t("bank.briefing.capacite")}.</span>{" "}
        {fill("bank.briefing.capaciteLigne", {
          duree: termWords(operands) ?? "",
          pct: tenths(operands.capacityUsedPct),
        })}
        {operands.capacityWords === null ? "" : ` ${asSentence(operands.capacityWords)}`}
        {proposed === null ? null : ` ${proposed}`}
      </Callout>
    ) : null;
  // D2 frame 18: the grid has no row for some notes; the rate in use and the letters come from the server's fields.
  const grid =
    missing.length > 0 && highest !== null && operands !== null && operands.ratePct !== null ? (
      <Callout
        tone="warn"
        icon={<AlertTriangle className="h-5 w-5" aria-hidden="true" />}
        testId="bank-briefing-attention-grille"
      >
        <b>{t("bank.briefing.grilleIncomplete")}</b>{" "}
        {fill(
          letter !== null && missing.includes(letter)
            ? "bank.briefing.grilleSansOffre"
            : "bank.briefing.grilleAvecOffre",
          {
            note: highest.grade,
            taux: operands.ratePct.replace(".", ","),
            notes: t(missing.length > 1 ? "bank.briefing.lesNotes" : "bank.briefing.laNote"),
            liste: missing.join(" et "),
            lettre: letter ?? "",
          },
        )}
      </Callout>
    ) : null;
  const nothing =
    capacity === null &&
    grid === null &&
    blocking.length === 0 &&
    incomplete === null &&
    facts !== null &&
    facts.pointsToCheck.toCheck === 0;
  const count = COUNT_WORDS.get(points) ?? String(points);
  const state =
    incomplete !== null
      ? t("bank.briefing.etatIncomplete")
      : analysis.grade?.provisional === true
        ? t("bank.briefing.etatProvisoire")
        : analysis.run.points !== undefined &&
            analysis.run.points.withEvidence === analysis.run.points.expected
          ? t("bank.briefing.etatToutesPreuves")
          : "";
  return (
    <Card className="p-5" dataKit="attention">
      <p className="mb-3 text-base font-semibold text-gray-900" data-testid="bank-briefing-attention-titre">
        {t("bank.briefing.attentionTitre")}
      </p>
      <div className="grid gap-3">
        {grid}
        {capacity}
        {blocking.map((one, index) => (
          <Callout
            key={`${String(index)}-${one.reason}`}
            tone="warn"
            icon={<AlertTriangle className="h-5 w-5" aria-hidden="true" />}
            testId={`bank-briefing-attention-bloquant-${String(index)}`}
          >
            <LabelledSentence label={t("bank.bay.gravBloquant")} words={one.reason} />
          </Callout>
        ))}
        {nothing ? (
          <Callout tone="ok" testId="bank-briefing-attention-rien">
            <span className="font-semibold">{t("bank.briefing.rienATitre")}</span>{" "}
            {fill("bank.briefing.rienACorps", {
              n: COUNT_WORDS.get(facts.pointsToCheck.read) ?? String(facts.pointsToCheck.read),
            })}
          </Callout>
        ) : null}
      </div>
      {points === 0 ? null : (
        <p className="mt-3 text-sm text-gray-600" data-testid="bank-briefing-attention-suite">
          {points === 1
            ? t("bank.briefing.explicationUn")
            : fill("bank.briefing.explication", {
                points: fill("bank.briefing.lesPoints", { n: count }),
                etat: state,
              })}
        </p>
      )}
    </Card>
  );
}

export function Briefing(props: {
  readonly file: BriefingFile;
  readonly analysis: AnalysisView | null;
  readonly standing: RunStanding;
  /** The sentence the door gives when the bank has no analysis for the file. */
  readonly sentence: string | null;
  readonly busy: boolean;
  readonly onPrendre: () => void;
  /**
   * D-1026-AA-4: the analyst may refuse at nouveau, by the SAME door and the SAME modal and motive list as at revue (`RefuseModal`, no second
   * form). Unbound where the screen has no door: no « Refuser » is drawn.
   */
  readonly onRefuse?: (reasonCode: string) => void;
  readonly refusalReasons?: readonly { readonly code: string; readonly label: string }[];
}): ReactElement {
  const { analysis, standing } = props;
  const [refuseOpen, setRefuseOpen] = useState<number>(0);
  const read = standing === "read" && analysis !== null;
  const facts = read ? analysis.briefing : null;
  const deposit = props.file.depositPct;
  const ltvPoint = read ? pointOf(analysis, "prixValeurExpertisee") : undefined;
  const ltv = read ? comparedOf(analysis, "prixValeurExpertisee", t("bank.briefing.cleLtv")) : null;
  const ltvCap = read ? comparedOf(analysis, "prixValeurExpertisee", t("bank.briefing.clePlafondLtv")) : null;
  const quand = formatDateTime(new Date(props.file.receivedAt));
  // D2's foot: « Reçu {date}, {heure} de Hakili · dossier complet, {n} pièces lues ». Both claims are the record's, not the screen's:
  // « dossier complet » , a dossier leaves `collecte` only with no pièce missing (`stage-machine.ts`, the `collecte` to `analyse` guard,
  // `facts.piecesManquantes > 0` refuses); « de Hakili » , the only insert into `lending.bank_files` is `createBankFile`
  // (`submission.service.ts:625`), reached only through the console's `POST /api/v1/dossiers/{id}/submissions`
  // (`dossiers.controller.ts:1750`, right `soumettreABanque`).
  const expectedPoints = analysis?.run.points?.expected;
  const reception =
    facts === null
      ? fill("bank.briefing.recuDeHakili", { quand })
      : fill("bank.briefing.recuDeHakiliPieces", { quand, pieces: documentsReadWords(facts) });
  return (
    <section className="space-y-4" data-testid="bank-briefing">
      {standing === "reading" ? (
        <Callout
          tone="info"
          icon={<Info className="h-5 w-5" aria-hidden="true" />}
          testId="bank-briefing-en-cours"
        >
          <b>{t("bank.hakili.enCoursTitre")}</b> {t("bank.hakili.enCoursSuite")}
        </Callout>
      ) : standing === "failed" ? (
        <Callout
          tone="warn"
          icon={<AlertTriangle className="h-5 w-5" aria-hidden="true" />}
          testId="bank-briefing-echec"
        >
          <b>{t("bank.hakili.echecTitre")}</b>{" "}
          {expectedPoints === undefined
            ? t("bank.hakili.echecSuiteSansNombre")
            : fill("bank.hakili.echecSuite", {
                n: COUNT_WORDS.get(expectedPoints) ?? String(expectedPoints),
              })}
        </Callout>
      ) : !read ? (
        <p className="text-sm text-gray-900" data-testid="bank-briefing-sans-analyse">
          {props.sentence === null ? t("bank.hakili.noteAbsente") : asSentence(props.sentence)}
        </p>
      ) : (
        <>
          <div
            className={facts === null ? "grid grid-cols-3 gap-3" : "grid grid-cols-4 gap-3"}
            data-testid="bank-briefing-tuiles"
          >
            <CapacityTile analysis={analysis} />
            {ltvPoint === undefined ? null : (
              <div className="flex min-w-0" data-testid="bank-briefing-tuile-ltv">
                <StatCard
                  className="min-w-0 flex-1"
                  label={t("bank.briefing.tuileLtv")}
                  value={unbreakable(ltv ?? t("common.valeur.tiret"))}
                  {...(ltv === null
                    ? ltvPoint.reason === ""
                      ? {}
                      : { hint: ltvPoint.reason }
                    : ltvCap === null
                      ? {}
                      : { hint: fill("bank.briefing.plafondBanque", { plafond: ltvCap }) })}
                />
              </div>
            )}
            {deposit === null ? null : (
              <div className="flex min-w-0" data-testid="bank-briefing-tuile-apport">
                <StatCard
                  className="min-w-0 flex-1"
                  label={t("bank.briefing.tuileApport")}
                  value={unbreakable(fill("bank.briefing.pct", { n: String(deposit) }))}
                  {...(facts === null ? {} : { hint: <ApportHint facts={facts} /> })}
                />
              </div>
            )}
            {facts === null ? null : <IncidentsTile facts={facts} />}
          </div>
          <Attention analysis={analysis} />
        </>
      )}
      <ActionBar label={t("bank.revue.barreActions")} testId="bank-briefing-pied">
        <span className="text-[15px] text-[color:var(--hk-text-2)]">{reception}</span>
        <span className="flex-1" />
        {props.onRefuse === undefined ? null : (
          <button
            type="button"
            disabled={props.busy}
            onClick={() => {
              setRefuseOpen((count) => count + 1);
            }}
            className="rounded-xl px-3.5 py-2.5 text-sm font-semibold text-[color:var(--hk-bad)] hover:bg-[color:var(--hk-bad-bg)] disabled:opacity-40"
            data-testid="bank-revue-refuser"
          >
            {t("bank.revue.refuser")}
          </button>
        )}
        <Button
          variant="primary"
          size="lg"
          onClick={props.onPrendre}
          disabled={props.busy}
          className="inline-flex items-center gap-2 whitespace-nowrap"
          data-testid="bank-revue-prendre"
        >
          {t("bank.revue.prendre")}
          <ArrowRight className="h-4 w-4" aria-hidden="true" />
        </Button>
      </ActionBar>
      {refuseOpen > 0 && props.onRefuse !== undefined ? (
        <RefuseModal
          key={refuseOpen}
          open
          reasons={props.refusalReasons ?? []}
          busy={props.busy}
          onClose={() => {
            setRefuseOpen(0);
          }}
          onConfirm={(reasonCode) => {
            setRefuseOpen(0);
            props.onRefuse?.(reasonCode);
          }}
        />
      ) : null}
    </section>
  );
}
